import { IFlightSegment, ISeat, ISeatMapSegment } from '@/shared/api/aviabooking'
import { useAppSelector } from '@/shared/lib/redux'
import React, { useState, useMemo, FC } from 'react'
import { useFormContext } from 'react-hook-form'
import { BookFlightFormValues, selectFlightSeatMap, selectFlightSegmentsMap } from '../../model'
import { selectAirportsMap } from '@/entities/aviabooking-reference'
import { getSeatCharacteristicLabels, hasSeatCharacteristic } from '../../lib/seat-characteristics'

interface SeatMapProps {
    travellerIndex: number
}

const SEAT_TYPES = {
    W: { label: 'Окно', color: 'bg-blue-500', selectedColor: 'bg-blue-600' },
    NPW: { label: 'Нет окна', color: 'bg-gray-500', selectedColor: 'bg-gray-600' },
    A: { label: 'У прохода', color: 'bg-green-500', selectedColor: 'bg-green-600' },
    default: { label: 'Стандартное', color: 'bg-gray-400', selectedColor: 'bg-gray-500' }
}

interface SegmentSeatMapProps {
    segment: ISeatMapSegment
    segmentIndex: number
    travellerIndex: number
    segmentsMap: Record<string, IFlightSegment>
}

const SegmentSeatMap: React.FC<SegmentSeatMapProps> = ({ 
    segment, 
    segmentIndex, 
    travellerIndex, 
    segmentsMap 
}) => {
    const { setValue, getValues } = useFormContext<BookFlightFormValues>()
    const airportsMap = useAppSelector(selectAirportsMap)
    const [showLegend, setShowLegend] = useState(true)
    const selectedSeats = getValues(`travellers.${travellerIndex}.selected_seats`)
    const selectedSeat = selectedSeats[segmentIndex]

    const handleSeatClick = (rowNumber: number, segmentRef: number, seat: ISeat) => {
        if (!seat.is_free || seat.not_exists) return
        
        const selectedSeats = getValues(`travellers.${travellerIndex}.selected_seats`)
        const newSelectedSeats = {...selectedSeats}
        newSelectedSeats[segmentIndex] = {
            ...seat,
            rowNumber,
            segmentRef
        }
        setValue(`travellers.${travellerIndex}.selected_seats`, newSelectedSeats)
    }

    const getSeatTypeConfig = (type: string) => {
        return SEAT_TYPES[type as keyof typeof SEAT_TYPES] || SEAT_TYPES.default
    }

    const segmentStats = useMemo(() => {
        let totalSeats = 0
        let availableSeats = 0
        let totalPrice = 0

        segment.floors.forEach((floor: any) => {
            floor.rows.forEach((row: any) => {
                row.seats.forEach((seat: ISeat) => {
                    if (!seat.not_exists) {
                        totalSeats++
                        if (seat.is_free) {
                            availableSeats++
                            totalPrice += seat.price.amount
                        }
                    }
                })
            })
        })

        const averagePrice = availableSeats > 0 ? totalPrice / availableSeats : 0

        return { totalSeats, availableSeats, averagePrice }
    }, [segment])

    const selectedSeatData = useMemo(() => {
        if (!selectedSeat) return null

        for (const floor of segment.floors) {
            for (const row of floor.rows) {
                if (row.number !== selectedSeat.rowNumber) continue
                const foundSeat = row.seats.find(seat => seat.number === selectedSeat.number)
                if (foundSeat) return { seat: foundSeat, row }
            }
        }
        
        return null;
    }, [selectedSeat, segment])

    const segmentInfo = segmentsMap[segment.segment_number]

    return (
        <div className="mb-8 p-6 bg-white border border-gray-200 rounded-xl">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-lg font-bold text-gray-800">
                        Сегмент {segmentIndex + 1}: {segmentInfo && 
                            `${airportsMap[segmentInfo.dep_airport_code]?.city} → ${airportsMap[segmentInfo.arr_airport_code]?.city}`
                        }
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                        Выберите место для этого сегмента перелета
                    </p>
                </div>
                <div className="flex items-center space-x-4">
                    <button
                        type="button"
                        onClick={() => setShowLegend(!showLegend)}
                        className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                        {showLegend ? 'Спрятать легенду' : 'Показать легенду'}
                    </button>
                    <div className="text-sm text-gray-600">
                        {segmentStats.availableSeats} из {segmentStats.totalSeats} мест доступно
                    </div>
                </div>
            </div>

            {showLegend && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-semibold text-gray-700 mb-3">Типы мест</h3>
                    <div className="flex flex-wrap gap-4">
                        {Object.entries(SEAT_TYPES).map(([key, config]) => (
                            <div key={key} className="flex items-center space-x-2">
                                <div className={`w-4 h-4 rounded ${config.color}`}></div>
                                <span className="text-sm text-gray-600">{config.label}</span>
                            </div>
                        ))}
                        <div className="flex items-center space-x-2">
                            <div className="w-4 h-4 rounded bg-red-300 border-2 border-red-500"></div>
                            <span className="text-sm text-gray-600">Забронированные</span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <div className="w-4 h-4 rounded bg-gray-800"></div>
                            <span className="text-sm text-gray-600">Выбранные</span>
                        </div>
                    </div>
                </div>
            )}

            {selectedSeatData && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h3 className="font-semibold text-blue-800 mb-2">Выбранное место для этого сегмента</h3>
                    <div className="flex justify-between items-center">
                        <div>
                            <span className="text-lg font-bold text-blue-900">
                                Место {selectedSeatData.row.number}-{selectedSeatData.seat.number}
                            </span>
                            <div className="text-sm text-blue-700">
                                {getSeatCharacteristicLabels(selectedSeatData.seat.characteristics).join(' • ')}
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-lg font-bold text-blue-900">
                                {selectedSeatData.seat.price.amount} {selectedSeatData.seat.price.currency}
                            </div>
                            <div className="text-sm text-blue-700 capitalize">
                                {selectedSeatData.seat.type.toLowerCase()} место
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="space-y-6">
                {segment.floors.map((floor, floorIndex) => (
                    <div key={floorIndex} className="bg-gray-50 p-6 rounded-xl">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-semibold text-gray-700">
                                {floor.is_upper ? 'Верхняя палуба' : 'Нижняя палуба'}
                            </h3>
                            <div className="text-sm text-gray-500">
                                Ряд {floor.rows[0]?.number} - {floor.rows[floor.rows.length - 1]?.number}
                            </div>
                        </div>

                        <div className="space-y-3">
                            {floor.rows.map((row) => (
                                <div key={row.number} className="flex items-center space-x-4">
                                    <div className="w-12 text-sm font-medium text-gray-600 text-center">
                                        {row.number}
                                    </div>

                                    <div className="flex-1 flex items-center justify-center space-x-2">
                                        {row.seats.map((seat: ISeat) => {
                                            const isSelected = selectedSeat?.rowNumber === row.number && seat.number === selectedSeat?.number
                                            const seatType = getSeatTypeConfig(seat.type)

                                            if (seat.not_exists) {
                                                return <div key={seat.number} className="w-12" />
                                            }

                                            return (
                                                <button
                                                    key={seat.number}
                                                    type="button"
                                                    onClick={() => handleSeatClick(row.number, segment.segment_number, seat)}
                                                    disabled={!seat.is_free}
                                                    className={`
                                                        relative w-12 h-12 rounded-lg border-2 transition-all duration-200
                                                        flex items-center justify-center font-semibold text-white
                                                        ${!seat.is_free
                                                            ? 'bg-red-300 border-red-500 cursor-not-allowed'
                                                            : isSelected
                                                                ? `bg-gray-800 border-gray-900 transform scale-110 shadow-lg ${seatType.selectedColor}`
                                                                : `hover:scale-105 hover:shadow-md ${seatType.color} border-${seatType.color}`
                                                        }
                                                    `}
                                                >
                                                    {seat.number}
                                                    {hasSeatCharacteristic(seat.characteristics, 'IE') && (
                                                        <div className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-400 rounded-full" />
                                                    )}
                                                </button>
                                            )
                                        })}
                                    </div>

                                    <div className="w-12 text-sm font-medium text-gray-600 text-center">
                                        {row.number}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {!selectedSeat && (
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-700 text-center">
                        Место для этого сегмента еще не выбрано
                    </p>
                </div>
            )}
        </div>
    )
}

export const SeatMap: FC<SeatMapProps> = ({ travellerIndex }) => {
    const seatMap = useAppSelector(selectFlightSeatMap)
    const segmentsMap = useAppSelector(selectFlightSegmentsMap)
    // Рейс без карты мест — рабочий случай: показываем пустое состояние, а не роняем страницу.
    const segments = seatMap?.segments ?? []

    return (
        <div className="w-full bg-white">
            <div className="space-y-6">
                {segments.map((segment, segmentIndex) => (
                    <SegmentSeatMap
                        key={segment.segment_number}
                        segment={segment}
                        segmentIndex={segmentIndex}
                        travellerIndex={travellerIndex}
                        segmentsMap={segmentsMap}
                    />
                ))}
            </div>

            {segments.length === 0 && (
                <div className="text-center py-12 bg-gray-50 rounded-xl">
                    <p className="text-gray-500">Нет доступных данных о местах для этого рейса</p>
                </div>
            )}
        </div>
    )
}